import * as React from 'react';
import { useSelector, useStore } from 'react-redux';
import * as Ably from 'ably';

import { HUB_BACKEND_ENVIRONMENTS, HubBackendEnvironmentUrls } from '~/main/environments';
import { LoggerScope } from '~/main/utils/logger';
import { useSocket } from '~/renderer/hooks/socket/use-setup-socket';
import { cfdActions } from '~/renderer/store/cfd';
import { getCfdOptions } from '~/renderer/store/cfd/selectors';
import { connectionActions } from '~/renderer/store/connection';
import {
  CONNECTION_STATUS,
  CustomerConnectionStatusType,
  MEMBER_TYPE,
} from '~/renderer/store/connection/types';
import { getIsFullscreen } from '~/renderer/store/displays/selectors';
import { getHubBackendEnvironmentUrl } from '~/renderer/store/hub/selectors';
import { receiptActions } from '~/renderer/store/receipt';
import { getSale } from '~/renderer/store/receipt/selectors';
import { isBrowserCFD, isCustomerCFD, isRenderProcess } from '~/renderer/utils';
import Logger from '~/renderer/utils/logger';

import { CFD_MESSAGES, Publish } from './types';

import type { ConnectionState } from '~/renderer/store/connection/types';
import type { AppState } from '~/renderer/store/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MessageData<Data = any> = { data?: Data };

export const useSetupAblyCustomer = () => {
  const [publishCustomer, setPublishCustomer] = React.useState<Publish | null>(null);
  const store = useStore();
  const publish = useSocket();

  const isHubCFD = useSelector(getIsFullscreen);
  const hubBackendEnvironmentUrl = useSelector(getHubBackendEnvironmentUrl);
  const { channelNameCustomer } = useSelector<AppState, ConnectionState>(state => state.connection);
  const sale = useSelector(getSale);
  const options = useSelector(getCfdOptions);

  const isMemberConnected = (
    member: MEMBER_TYPE,
    members: Ably.Types.PresenceMessage[] | undefined,
  ) => {
    return members?.some(({ clientId }) => clientId === member);
  };

  React.useEffect(() => {
    if (!channelNameCustomer) {
      return;
    }

    let baseAuthUrl = hubBackendEnvironmentUrl || process.env.HUB_BACKEND_URL;
    if (
      !hubBackendEnvironmentUrl &&
      !isRenderProcess &&
      (window.location.hostname.includes('lightspeedappstaging') ||
        window.location.hostname.includes('lightspeedappstg'))
    ) {
      // Enable Staging CFD static assets to connect to Staging hub-backend
      baseAuthUrl = HubBackendEnvironmentUrls[HUB_BACKEND_ENVIRONMENTS.STAGING];
    }

    const client = new Ably.Realtime({
      clientId: isCustomerCFD ? MEMBER_TYPE.Customer : MEMBER_TYPE.CFD,
      log: {
        level: 2,
        handler: message => {
          const [, , newMessage] = message.match(/(Ably:\s+)(.+)/) || [];
          return Logger({ scope: LoggerScope.ABLY_CUSTOMER }).debug({
            msg: newMessage || message,
          });
        },
      },
      authUrl: `${baseAuthUrl}api/socket/auth`,
    });
    const channel = client?.channels.get(`private:${channelNameCustomer}`);
    function publishToAbly(message: string, messageData?: Record<string, unknown> | string) {
      channel?.presence.get((errorInfo, members) => {
        if (errorInfo) {
          handleError(errorInfo);
          return;
        }

        // If the Hub is the only member of the channel, do not send messages on the channel
        if (members?.length === 1) {
          Logger({ scope: LoggerScope.ABLY_CUSTOMER }).debug({
            msg: `CFD is alone in channel: ${channel}`,
            message,
          });
          return false;
        }

        Logger({ scope: LoggerScope.ABLY_CUSTOMER }).debug({
          msg: `Publish`,
          message,
          messageData,
        });

        channel?.publish(message, messageData);
        return true;
      });
    }

    client.connection.once(CONNECTION_STATUS.CONNECTED, onceConnected);
    client.connection.on(CONNECTION_STATUS.CONNECTED, onConnected);

    function onceConnected() {
      channel?.presence.subscribe('enter', onPresenceMembersUpdated);
      channel?.presence.subscribe('leave', onPresenceMembersUpdated);

      if (isCustomerCFD) {
        channel.subscribe(CFD_MESSAGES.SALE_UPDATED, (message: MessageData) => {
          const { sale, options } = message.data;

          store.dispatch(receiptActions.updateSale({ sale, saleId: sale?.saleID }));
          store.dispatch(cfdActions.updateOptions(options));

          Logger({ scope: LoggerScope.CFD, transport: LoggerScope.ABLY_CUSTOMER }).debug({
            msg: CFD_MESSAGES.SALE_UPDATED,
            saleId: sale?.saleID,
          });
        });
      } else {
        channel.subscribe(CFD_MESSAGES.CUSTOMER_SAVE, (message: MessageData) => {
          const { data } = message;

          Logger({ scope: LoggerScope.CFD, transport: LoggerScope.ABLY_CUSTOMER }).debug({
            msg: CFD_MESSAGES.CUSTOMER_SAVE,
            customer: data,
          });

          store.dispatch(
            receiptActions.setCustomerInfoVisibility({
              isCustomerInfoViewVisible: false,
              isSaleVisible: true,
            }),
          );

          publish(CFD_MESSAGES.CUSTOMER_SAVE, data);
        });

        channel.subscribe(CFD_MESSAGES.CUSTOMER_CANCEL, () => {
          Logger({ scope: LoggerScope.CFD, transport: LoggerScope.ABLY_CUSTOMER }).debug({
            msg: CFD_MESSAGES.CUSTOMER_CANCEL,
          });

          store.dispatch(
            receiptActions.setCustomerInfoVisibility({
              isCustomerInfoViewVisible: false,
              isSaleVisible: true,
            }),
          );

          publish(CFD_MESSAGES.CUSTOMER_CANCEL);
        });
      }
    }

    function onPresenceMembersUpdated() {
      channel?.presence.get((_errorInfo, members) => {
        let status: CustomerConnectionStatusType = 'disconnected';
        if (isCustomerCFD) {
          status = isMemberConnected(MEMBER_TYPE.CFD, members) ? 'connected' : 'unavailable';
        } else if (isBrowserCFD || isHubCFD) {
          status = isMemberConnected(MEMBER_TYPE.Customer, members) ? 'connected' : 'disconnected';

          if (status === 'connected') {
            channel?.publish(CFD_MESSAGES.SALE_UPDATED, { sale, options });
          }
        }

        store.dispatch(connectionActions.updateAblyCustomerConnectionStatus({ status }));
      });
    }

    function onConnected(event: Ably.Types.ConnectionStateChange) {
      Logger({ scope: LoggerScope.ABLY_CUSTOMER }).debug({
        msg: 'Ably Customer connected',
        ...event,
      });

      channel?.presence.enter();
    }

    setPublishCustomer(() => publishToAbly);

    return () => {
      client?.close();
      channel?.presence.leave();
      channel?.unsubscribe();
      store.dispatch(
        connectionActions.updateAblyCustomerConnectionStatus({ status: 'disconnected' }),
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelNameCustomer]);

  return publishCustomer;
};

function handleError(errorInfo: Ably.Types.ErrorInfo) {
  if (errorInfo.code === 400 || errorInfo.code === 401) {
    Logger({ scope: LoggerScope.APP }).debug({
      msg: 'Ably presence get error',
      errorInfo,
    });
    return;
  }

  Logger({ scope: LoggerScope.APP }).error({
    msg: errorInfo.message,
    err: errorInfo,
  });
}
